import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import styled from 'styled-components';
import { FiGithub } from 'react-icons/fi';
import ProjectPreview from './ProjectPreview';
import FlowDiagram from './FlowDiagram';
import { ArchitecturePair } from './ArchitectureGraph';

const ProjectSection = styled(motion.div)`
  padding: 4rem 0;
  border-bottom: 1px solid var(--border-color);
  
  &:first-child {
    padding-top: 2rem;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;


const ProjectVisual = styled.div`
  width: 100%;
  height: 350px;
  border-radius: var(--radius-md);
  position: relative;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
  transition: box-shadow 0.4s ease;
  
  &:hover {
    box-shadow: var(--shadow-float);
  }
`;

const ProjectContent = styled.div`
  padding: 1rem 0;
`;

const RoleLine = styled.div`
  font-size: 0.85rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
`;

const ProjectName = styled.h3`
  font-size: 3.5rem;
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -0.03em;
  line-height: 0.9;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ProjectTagline = styled.p`
  font-size: 1.15rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
`;

const TagContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  padding: 0.3rem 0.8rem;
  background: ${props => props.color || '#f0f0f0'};
  color: ${props => props.textColor || 'var(--text-primary)'};
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 4px;
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
`;

const DetailItem = styled.li`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0.75rem 0;
  padding-left: 1.5rem;
  position: relative;
  
  &::before {
    content: '→';
    position: absolute;
    left: 0;
    color: var(--text-muted);
  }
`;

const MetricsRow = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const Metric = styled.div`
  flex: 1;
`;

const MetricValue = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${props => props.highlight ? 'var(--text-primary)' : 'var(--text-secondary)'};
  margin-bottom: 0.25rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-wrap: balance;
`;

const ViewProjectButton = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: fit-content;
  padding: 0.7rem 1.5rem;
  background: transparent;
  border: 2px solid var(--text-primary);
  border-radius: var(--radius-full);
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--text-primary);
    border-color: var(--text-primary);
    color: white;
  }
`;

// Removed old FlowDiagram styled component - using new FlowDiagram component instead

const CountUp = ({ end, duration = 1000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);
  
  // Grouped: a five-figure metric reads as 50000 without this, and the site's own copy already
  // writes these with separators ("10,000+ datasets").
  return <span ref={ref}>{count.toLocaleString()}</span>;
};

// Removed old FlowNode and FlowArrow components - using new FlowDiagram component instead

const MinimalCaseStudy = ({ project }) => {
  const tagColors = {
    'React': { bg: '#61DAFB', text: '#000' },
    'Node.js': { bg: '#68A063', text: '#fff' },
    'TypeScript': { bg: '#3178C6', text: '#fff' },
    'JavaScript': { bg: '#F7DF1E', text: '#000' },
    'Swift': { bg: '#F05138', text: '#fff' },
    'Rust': { bg: '#CE422B', text: '#fff' },
    'Python': { bg: '#3776AB', text: '#fff' },
    'PyTorch': { bg: '#EE4C2C', text: '#fff' },
    'Three.js': { bg: '#000000', text: '#fff' },
    'AI/ML': { bg: '#FF6B6B', text: '#fff' },
    'Azure Databricks': { bg: '#FF3621', text: '#fff' },
    'Databricks Genie': { bg: '#1B3139', text: '#fff' },
  };
  
  return (
    <ProjectSection
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <ProjectGrid>
        <div>
          <ProjectVisual>
            {project.visual || (
              <ProjectPreview
                liveUrl={project.liveUrl}
                previewUrl={project.previewUrl}
                projectName={project.name}
                projectIcon={project.icon}
                backgroundColor={project.visualBg}
                showBrowserChrome={false}
                allowIframe={project.allowIframe || false}
              />
            )}
          </ProjectVisual>
          {(project.liveUrl || project.githubUrl) && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap',
                marginTop: '1.25rem'
              }}
            >
              {project.liveUrl && (
                <ViewProjectButton
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Live Site →
                </ViewProjectButton>
              )}
              {project.githubUrl && (
                <ViewProjectButton
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiGithub /> Open source
                </ViewProjectButton>
              )}
            </div>
          )}
        </div>
        
        <ProjectContent>
          {project.role && <RoleLine>{project.role}</RoleLine>}
          <ProjectName>{project.name}</ProjectName>
          <ProjectTagline>{project.tagline}</ProjectTagline>
          
          <TagContainer>
            {project.tags.map((tag, idx) => {
              const colors = tagColors[tag] || {};
              return (
                <Tag 
                  key={idx} 
                  color={colors.bg}
                  textColor={colors.text}
                >
                  {tag}
                </Tag>
              );
            })}
          </TagContainer>
          
          <DetailsList>
            {project.details.map((detail, idx) => {
              // Parse and bold the label (Problem:, Solution:, Impact:)
              const parts = detail.split(':');
              if (parts.length > 1) {
                return (
                  <DetailItem key={idx}>
                    <strong>{parts[0]}:</strong>{parts.slice(1).join(':')}
                  </DetailItem>
                );
              }
              return <DetailItem key={idx}>{detail}</DetailItem>;
            })}
          </DetailsList>
          
          <MetricsRow>
            {project.metrics.map((metric, idx) => (
              <Metric key={idx}>
                <MetricValue highlight={idx === 0}>
                  {metric.hasCount ? (
                    <>
                      <CountUp end={metric.value} />
                      {metric.suffix}
                    </>
                  ) : (
                    <>
                      {metric.value}{metric.suffix}
                    </>
                  )}
                </MetricValue>
                <MetricLabel>{metric.label}</MetricLabel>
              </Metric>
            ))}
          </MetricsRow>
        </ProjectContent>
      </ProjectGrid>
      
      {project.architectures && <ArchitecturePair graphs={project.architectures} />}

      {!project.architectures && project.showFlow && project.flowData && (
        <FlowDiagram 
          nodes={project.flowData.nodes} 
          connections={project.flowData.connections}
        />
      )}
      
    </ProjectSection>
  );
};

export default MinimalCaseStudy;